import React, { Component } from 'react';
import PropTypes from 'prop-types';
import Chart from '../chart/Chart';
import { 
  BULLET_CHART_TYPE, 
  HORIZONTAL_BULLET_CHART_TYPE, 
  LINE_CHART_TYPE, 
  KAPLAN_MEIER_CHART_TYPE, 
  STACKED_BULLET_CHART_TYPE, 
  HORIZONTAL_STACKED_BULLET_CHART_TYPE,
  HORIZONTAL_CHART_TYPES
} from '../chart/chartFunctions';
import { Grid, GridItem } from '../grid/Grid';
import ButtonInput from '../button-input/ButtonInput';
import CheckboxInput from '../checkbox-input/CheckboxInput';
import SelectBox from '../select-box/SelectBox';
import TextInput from '../text-input/TextInput';
import ImageInput from '../image-input/ImageInput';
import InputUpload from '../input-upload/InputUpload';
import { unparse } from '../../../functions/parse-handling';
import './ChartInput.css';

export default class ChartInput extends Component {
  constructor(props) {
    super(props);

    this.state = {
      chartInputValues: (this.props.existingContentObject) ? this.props.existingContentObject : {},
      type: (this.props.existingContentObject) ? this.props.existingContentObject.type : undefined,
      savedChartInputValues: (this.props.existingContentObject) ? this.props.existingContentObject : {},
      savedType: (this.props.existingContentObject) ? this.props.existingContentObject.type : undefined,
      tempChartInputValues: {}
    };
  }

  UNSAFE_componentWillReceiveProps(newProps) {
    const newChartInputValues = (newProps.existingContentObject) ? newProps.existingContentObject : {};
    const newType = (newProps.existingContentObject) ? newProps.existingContentObject.type : undefined;

    // On toggle into edit mode
    if (!this.props.isEditable && newProps.isEditable) {
      this.setState({
        chartInputValues: newChartInputValues,
        type: newType,
        tempChartInputValues: {}
      });
    }
    // On toggle into read-only mode
    else if (this.props.isEditable && !newProps.isEditable) {
      this.setState({
        savedChartInputValues: newChartInputValues,
        savedType: newType
      });
    }
  }

  // Grabs all of the input values in an object
  getValue = () => {
    let valuesObject = {};
    this.addChartValuesToObject(valuesObject);
    valuesObject.type = this.state.type;

    for (let property in valuesObject) {
      if (valuesObject[property] === "") {
        delete valuesObject[property];
      }
    }

    this.setState({ chartInputValues: valuesObject });

    return valuesObject;
  }

  // Updates the type of the chart while preserving the current inputs in a temp object
  updateType = () => {
    this.setState((prevState) => {
      let type = this[this.props.id + '-type'].getValue();

      let tempChartInputValues = prevState.tempChartInputValues;
      let currentInputValues = {};
      this.addChartValuesToObject(currentInputValues);

      let tempHasKeys = false;

      Object.keys(currentInputValues).forEach((key) => {
        if (currentInputValues[key] !== undefined) {
          tempHasKeys = true;
          tempChartInputValues[key] = currentInputValues[key];
        }
      });

      if (tempHasKeys) {
        return { type, tempChartInputValues };
      }

      return { type };
    });
  }

  // Updates what displays on the chart
  updateChart = () => {
    this.setState((prevState) => {
      let type = this[this.props.id + '-type'].getValue();
      let chartInputValues = prevState.chartInputValues;
      this.addChartValuesToObject(chartInputValues);

      return { type, chartInputValues, tempChartInputValues: {} };
    });
  }

  // Puts the input values in an object
  addChartValuesToObject = (object) => {
    object.betterArrowDirection = (this[this.props.id + '-better-arrow-direction']) ? this[this.props.id + '-better-arrow-direction'].getValue() : undefined;
    object.data = (this[this.props.id + '-chart-data']) ? this[this.props.id + '-chart-data'].getValue() : undefined;
    let imageValues = (this[this.props.id + '-image']) ? this[this.props.id + '-image'].getValue() : undefined;
    object.showLegend = (this[this.props.id + '-show-legend']) ? this[this.props.id + '-show-legend'].getValue() : undefined;
    object.legendOnTop = (this[this.props.id + '-legend-on-top']) ? this[this.props.id + '-legend-on-top'].getValue() : undefined;
    object.overallBenchmark = (this[this.props.id + '-overall-benchmark']) ? this[this.props.id + '-overall-benchmark'].getValue() : undefined;
    object.xAxisLabel = (this[this.props.id + '-x-axis-label']) ? this[this.props.id + '-x-axis-label'].getValue() : undefined;
    object.yMax = (this[this.props.id + '-y-max'] &&
      this[this.props.id + '-y-max'].getValue() !== "") ?
        parseFloat(this[this.props.id + '-y-max'].getValue()) :
        undefined;
    object.yMin = (this[this.props.id + '-y-min'] &&
      this[this.props.id + '-y-min'].getValue() !== "") ?
        parseFloat(this[this.props.id + '-y-min'].getValue()) :
        undefined;
    object.ySteps = (this[this.props.id + '-y-steps'] &&
      this[this.props.id + '-y-steps'].getValue()) ?
        parseFloat(this[this.props.id + '-y-steps'].getValue()) :
        undefined;
    object.yAxisLabel = (this[this.props.id + '-y-axis-label']) ? this[this.props.id + '-y-axis-label'].getValue() : undefined;
    if (imageValues) {
      object.image = {
        url: imageValues.imageURL,
        caption: imageValues.imageCaption,
        altText: imageValues.imageAlt,
        isGraphic: imageValues.imageIsGraphic
      };
    }
  }

  render() {
    // Controls what values are filled into the inputs
    let chartInputValues = (Object.keys(this.state.tempChartInputValues).length === 0) ?
      this.state.chartInputValues :
      this.state.tempChartInputValues;

    let chartViewValues, chartViewType;

    // Controls what values are being showed on the chart
    if (this.props.isEditable) {
      chartViewValues = this.state.chartInputValues;
      chartViewType = this.state.type;
    }
    else {
      chartViewValues = this.state.savedChartInputValues;
      chartViewType = this.state.savedType;
    }

    const betterArrowOptions = [
      {
        name: "None",
        value: ""
      },
      {
        name: "Up",
        value: "up"
      },
      {
        name: "Down",
        value: "down"
      }
    ];

    let overallBenchmarkCheckbox = (this.state.type !== LINE_CHART_TYPE && this.state.type !== KAPLAN_MEIER_CHART_TYPE) ?
      <GridItem>
        <CheckboxInput
          checked={chartInputValues.overallBenchmark}
          id={this.props.id + '-overall-benchmark'}
          label="Overall Benchmark"
          ref={instance => { this[this.props.id + '-overall-benchmark'] = instance; }}
        />
      </GridItem> :
      null;

    // Input display
    const chartFields = (this.props.isEditable) ? <div className="chart-inputs">
      <InputUpload
        buttonLabel="Upload CSV"
        existingContent={(chartInputValues.data) ? unparse(chartInputValues.data) : undefined}
        fileTypes=".csv"
        hasGrid={true}
        id={this.props.id + '-chart-data'}
        label="Chart Data"
        ref={instance => { this[this.props.id + '-chart-data'] = instance; }}
        verticalSpacing={this.props.verticalSpacing}
      />
      <Grid columns={2} isNestedGrid={true}>
        <GridItem>
          <TextInput
            existingContent={chartInputValues.xAxisLabel}
            id={this.props.id + '-x-axis-label'}
            label="X-Axis Title"
            ref={instance => { this[this.props.id + '-x-axis-label'] = instance; }}
            verticalSpacing={this.props.verticalSpacing}
          />
        </GridItem>
        <GridItem>
          <TextInput
            existingContent={chartInputValues.yAxisLabel}
            id={this.props.id + '-y-axis-label'}
            label="Y-Axis Title"
            ref={instance => { this[this.props.id + '-y-axis-label'] = instance; }}
            verticalSpacing={this.props.verticalSpacing}
          />
        </GridItem>
      </Grid>
      <fieldset>
        <legend>Y-Axis Value Range</legend>
        <Grid columns={3} isNestedGrid={true}>
          <GridItem>
            <TextInput
              existingContent={chartInputValues.yMin}
              id={this.props.id + '-y-min'}
              label="Minimum"
              ref={instance => { this[this.props.id + '-y-min'] = instance; }}
              type="number"
              verticalSpacing={this.props.verticalSpacing}
            />
          </GridItem>
          <GridItem>
            <TextInput
              existingContent={chartInputValues.yMax}
              id={this.props.id + '-y-axis-max-value'}
              label="Maximum"
              ref={instance => { this[this.props.id + '-y-max'] = instance; }}
              type="number"
              verticalSpacing={this.props.verticalSpacing}
            />
          </GridItem>
          <GridItem>
            <TextInput
              existingContent={chartInputValues.ySteps}
              id={this.props.id + '-y-axis-steps'}
              label="Increment"
              ref={instance => { this[this.props.id + '-y-steps'] = instance; }}
              type="number"
            />
          </GridItem>
        </Grid>
      </fieldset>
      <Grid columns={2} isNestedGrid={true}>
        <GridItem>
          <CheckboxInput
            checked={chartInputValues.showLegend}
            id={this.props.id + '-show-legend'}
            label="Show Legend"
            ref={instance => { this[this.props.id + '-show-legend'] = instance; }}
            verticalSpacing={this.props.verticalSpacing}
          />
        </GridItem>
        <GridItem>
          <CheckboxInput
            checked={chartInputValues.legendOnTop}
            id={this.props.id + '-legend-on-top'}
            label="Legend on Top"
            ref={instance => { this[this.props.id + '-legend-on-top'] = instance; }}
            verticalSpacing={this.props.verticalSpacing}
          />
        </GridItem>
        {overallBenchmarkCheckbox}
      </Grid>
      <SelectBox
        existingContent={chartInputValues.betterArrowDirection}
        id={this.props.id + '-better-arrow-direction'}
        label="Better Arrow"
        options={betterArrowOptions}
        ref={instance => { this[this.props.id + '-better-arrow-direction'] = instance; }} 
        verticalSpacing={this.props.verticalSpacing}
      />
      <ButtonInput
        className="button--small"
        value="Preview Changes"
        onClick={this.updateChart}
        verticalSpacing={this.props.verticalSpacing}
      />
    </div> :
    null;

    const typeOptions = [
      {
        name: "None",
        value: ""
      },
      {
        name: "Bar",
        value: BULLET_CHART_TYPE
      },
      {
        name: "Horizontal Bar",
        value: HORIZONTAL_BULLET_CHART_TYPE
      },
      {
        name: "Stacked Bar",
        value: STACKED_BULLET_CHART_TYPE
      },
      {
        name: "Horizontal Stacked Bar",
        value: HORIZONTAL_STACKED_BULLET_CHART_TYPE
      },
      {
        name: "Line",
        value: LINE_CHART_TYPE
      },
      {
        name: "Kaplan-Meier",
        value: KAPLAN_MEIER_CHART_TYPE
      },
      {
        name: "Image",
        value: "image"
      }
    ];

    const label = (this.props.label) ?
      <label htmlFor={this.props.id}>
        {this.props.label}
      </label> :
      null;
    
    let chartHeight = 520;
    
    if (HORIZONTAL_CHART_TYPES.includes(chartViewType)) {
      let dataLength = (chartViewValues.data && chartViewValues.data.length) ? chartViewValues.data.length : 1;
    
      let calcHeight = ((dataLength - 1) * 125) + 20;
    
      if (calcHeight > chartHeight) {
        chartHeight = calcHeight;
      }
    }

    let chartDisplay = null;

    if (chartViewType) {
      if (this.props.showChart) {
        chartDisplay = <Grid isNestedGrid={true} columns={2} layout={(!this.props.isEditable) ? "66-33" : null}>
          <GridItem>
            <Chart
              betterArrowDirection={chartViewValues.betterArrowDirection}
              data={chartViewValues.data}
              height={chartHeight}
              id={this.props.id + '-chart-display'}
              legendOnTop={chartViewValues.legendOnTop}
              overallBenchmark={chartViewValues.overallBenchmark}
              showLegend={chartViewValues.showLegend}
              type={chartViewType}
              width="100%"
              xAxisLabel={chartViewValues.xAxisLabel}
              yMax={chartViewValues.yMax}
              yMin={chartViewValues.yMin}
              ySteps={chartViewValues.ySteps}
              yAxisLabel={chartViewValues.yAxisLabel}
            />
          </GridItem>
          <GridItem>
            {chartFields}
          </GridItem>
        </Grid>;
      }
      else {
        chartDisplay = <Grid isNestedGrid={true} columns={2}>
          <GridItem>
          </GridItem>
          <GridItem>
            {chartFields}
          </GridItem>
        </Grid>;
      }
    }

    let chartInputDisplay = (this.props.isEditable) ?
      <div className="field chart-input">
        <SelectBox
          existingContent={this.state.type}
          id={this.props.id + '-type'}
          label="Chart Type"
          onChange={this.updateType}
          options={typeOptions}
          ref={instance => { this[this.props.id + '-type'] = instance; }}
          verticalSpacing={this.props.verticalSpacing}
        />
        {chartDisplay}
      </div> :
      <div className="field">
        {label}
        {chartDisplay}
      </div>;

    if (chartViewType === "image") {
      chartInputDisplay = (this.props.isEditable) ?
        <div className="field chart-input">
          <SelectBox
            existingContent={this.state.type}
            id={this.props.id + '-type'}
            label="Chart Type"
            onChange={this.updateType}
            options={typeOptions}
            ref={instance => { this[this.props.id + '-type'] = instance; }}
            verticalSpacing={this.props.verticalSpacing}
          />
          <ImageInput
            existingContentObject={(chartInputValues.image && chartInputValues.image.url) ?
              {
                imageURL: chartInputValues.image.url,
                imageCaption: chartInputValues.image.caption,
                imageAlt: chartInputValues.image.altText,
                imageIsGraphic: chartInputValues.image.isGraphic
              } :
              null
            }
            hasGraphicCheckbox={true}
            id={this.props.id + '-image'}
            isEditable={this.props.isEditable}
            ref={instance => { this[this.props.id + '-image'] = instance; }}
            verticalSpacing={this.props.verticalSpacing}
          />
        </div> :
        <div className="field">
          {label}
          <ImageInput
            existingContentObject={(chartInputValues.image && (chartInputValues.image.url ||
              chartInputValues.image.caption ||
              chartInputValues.image.altText ||
              chartInputValues.image.isGraphic)) ?
              {
                imageURL: chartInputValues.image.url,
                imageCaption: chartInputValues.image.caption,
                imageAlt: chartInputValues.image.altText,
                imageIsGraphic: chartInputValues.image.isGraphic
              } :
              null
            }
            hasGraphicCheckbox={true}
            id={this.props.id + '-image'}
            isEditable={this.props.isEditable}
            verticalSpacing={this.props.verticalSpacing}
          />
        </div>;
    }

    return chartInputDisplay;
  }
}

ChartInput.defaultProps = {
  showChart: true
};

ChartInput.propTypes = {
  existingContentObject: PropTypes.shape({
    type: PropTypes.oneOf([
      'bullet',
      'horizontal-bullet',
      'image',
      'kaplan-meier',
      'line',
      'stacked-bullet',
      'horizontal-stacked-bullet',
      ''
    ]),
    betterArrowDirection: PropTypes.oneOf(["up", "down", null]),
    data: PropTypes.arrayOf(
      PropTypes.arrayOf(
        PropTypes.oneOfType([
          PropTypes.number,
          PropTypes.string
        ])
      )
    ),
    legendOnTop: PropTypes.bool,
    showLegend: PropTypes.bool,
    xAxisLabel: PropTypes.string,
    yAxisLabel: PropTypes.string,
    yMax: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    yMin: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    ySteps: PropTypes.oneOfType([PropTypes.number, PropTypes.string])
  }),
  id: PropTypes.string,
  isEditable: PropTypes.bool,
  label: PropTypes.string,
  showChart: PropTypes.bool, //only exists so tests don't break
  verticalSpacing: PropTypes.string
};
